import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useEndUserDetail } from '../hooks/useClients';
import { PageHeader } from '../components/Navigation';
import { getBackPath } from '../utils/navigation';
import { apiFetch } from '../utils/api';
import '../InvoiceDetail.css';

export default function BeneficiarDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { data, status, error, refetch } = useEndUserDetail(id); // Use refetch from the hook
  const [isEditing, setIsEditing] = React.useState(false);
  const [form, setForm] = React.useState(null);
  const [saveError, setSaveError] = React.useState(null);

  if (status === 'loading' || status === 'idle') return <div className="invoice-detail"><main className="invoice-detail-main">Se încarcă...</main></div>;
  if (status === 'error') return <div className="invoice-detail"><main className="invoice-detail-main" style={{ color: '#b91c1c' }}>{error}</main></div>;
  if (!data) return null;

  const { end_user, client } = data;
  const backPath = getBackPath(location, client ? `/clients/${client.id}` : '/clients');

  const breadcrumbs = [
    { label: 'Validare', to: '/validation-hub' },
    { label: 'Emitenți', to: '/clients' },
    { label: client?.nume || 'Emitent', to: client ? `/clients/${client.id}` : '/clients' },
    { label: end_user.nume || 'Beneficiar', to: null }
  ];

  const startEdit = () => {
    setForm({
      nume: end_user.nume || '',
      // --- NEW: Use a single 'identifier' field in the form ---
      identifier: end_user.cui || end_user.cnp || '',
      nr_reg_com: end_user.nr_reg_com || '',
      adresa: end_user.adresa || '',
      oras: end_user.oras || '',
      judet: end_user.judet || '',
      tara_cod: end_user.tara_cod || 'RO',
    });
    setIsEditing(true);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setForm(null);
    setSaveError(null);
  };

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const saveEdit = async () => {
    setSaveError(null);
    try {
      // Prepare payload - preserve RO prefix for CUI if present
      const payload = { ...form };
      const rawIdentifier = (payload.identifier || '').trim().toUpperCase();

      // Check if it starts with RO - if so, preserve it
      let identifier;
      if (rawIdentifier.startsWith('RO')) {
        // Keep RO prefix, clean only the numeric part
        const digits = rawIdentifier.slice(2).replace(/\D/g, '');
        identifier = digits ? `RO${digits}` : '';
      } else {
        // No RO prefix - extract only digits
        identifier = rawIdentifier.replace(/\D/g, '');
      }

      // 13 digits = CNP (personal ID), otherwise = CUI (company ID)
      if (identifier && identifier.replace(/\D/g, '').length === 13 && !identifier.startsWith('RO')) {
        payload.cnp = identifier;
        payload.cui = null;
      } else {
        payload.cui = identifier;
        payload.cnp = null;
      }
      delete payload.identifier;

      const res = await apiFetch(`/api/end-users/${end_user.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.msg || 'Eroare la salvare.');
      }
      
      // Instead of setDetail, we'll refetch the data to get the latest version
      await refetch();
      setIsEditing(false);
      setForm(null);
    } catch (e) {
      setSaveError(e.message);
    }
  };

  return (
    <div className="invoice-detail">
      <PageHeader
        backTo={backPath}
        backLabel="Înapoi la Emitent"
        title="Beneficiar"
        subtitle={`${end_user.nume || '—'}${end_user.cui || end_user.cnp ? ` • ${end_user.cui || end_user.cnp}` : ''}`}
        breadcrumbs={breadcrumbs}
      />

      <main className="invoice-detail-main">
        <div className="invoice-detail-content">
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Detalii Beneficiar</h2>
              <p className="section-subtitle">Toate câmpurile din baza de date</p>
              <div style={{ marginTop: '0.5rem' }}>
                {!isEditing ? (
                  <button className="review-btn" onClick={startEdit}>Editează</button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="review-btn" onClick={cancelEdit}>Renunță</button>
                    <button className="review-btn" onClick={saveEdit}>Salvează</button>
                  </div>
                )}
              </div>
            </div>
            <div className="section-content">
              {!isEditing ? (
                <div className="form-grid form-grid-2">
                  <Field label="ID" value={end_user.id} mono />
                  <Field label="ID Client" value={end_user.id_client} mono />
                  <Field label="Denumire" value={end_user.nume || '—'} />
                  
                  {/* --- NEW: Conditional rendering for CUI/CNP --- */}
                  {end_user.cnp ? (
                    <Field label="CNP" value={end_user.cnp} mono />
                  ) : (
                    <Field label="CUI" value={end_user.cui || '—'} mono />
                  )}
                  {/* --- END OF NEW LOGIC --- */}

                  <Field label="Nr. Reg. Com." value={end_user.nr_reg_com || '—'} mono />
                  <Field label="Țară" value={end_user.tara_cod || '—'} />
                  <Field label="Județ" value={end_user.judet || '—'} />
                  <Field label="Oraș" value={end_user.oras || '—'} />
                  <Field label="Adresă" value={end_user.adresa || '—'} full />
                  <Field label="Stare" value={end_user.is_placeholder ? 'Necomplet' : 'Complet'} />
                  <Field label="Creat la" value={formatDateTime(end_user.created_at)} />
                  <Field label="Actualizat la" value={formatDateTime(end_user.updated_at)} />
                </div>
              ) : (
                <div className="form-grid form-grid-2">
                  <InputField label="Denumire" value={form.nume} onChange={(v) => handleChange('nume', v)} />
                  
                  {/* --- NEW: Single input for either CUI or CNP --- */}
                  <InputField label="CUI / CNP" value={form.identifier} onChange={(v) => handleChange('identifier', v)} mono />
                  {/* --- END OF NEW LOGIC --- */}

                  <InputField label="Nr. Reg. Com." value={form.nr_reg_com} onChange={(v) => handleChange('nr_reg_com', v)} mono />
                  <InputField label="Țară" value={form.tara_cod} onChange={(v) => handleChange('tara_cod', v)} />
                  <InputField label="Județ" value={form.judet} onChange={(v) => handleChange('judet', v)} />
                  <InputField label="Oraș" value={form.oras} onChange={(v) => handleChange('oras', v)} />
                  <InputField label="Adresă" value={form.adresa} onChange={(v) => handleChange('adresa', v)} full />
                </div>
              )}
              {saveError && <div style={{ color: '#b91c1c', marginTop: '0.5rem' }}>{saveError}</div>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, full = false, mono = false }) {
  return (
    <div className={full ? 'form-field party-address' : 'form-field'}>
      <div className="form-field-label">{label}</div>
      <div className={`form-field-value ${mono ? 'form-field-value-mono' : ''}`}>{String(value)}</div>
    </div>
  );
}

function InputField({ label, value, onChange, full = false, mono = false }) {
  return (
    <div className={full ? 'form-field party-address' : 'form-field'}>
      <div className="form-field-label">{label}</div>
      <input
        className={`clients-input ${mono ? 'form-field-value-mono' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const pad = (n) => String(n).padStart(2, '0');
    const dd = pad(d.getDate());
    const mm = pad(d.getMonth() + 1);
    const yyyy = d.getFullYear();
    const HH = pad(d.getHours());
    const MM = pad(d.getMinutes());
    return `${dd}.${mm}.${yyyy} ${HH}:${MM}`;
  } catch (_) {
    return value;
  }
}
